// ---------------------------------------------------------------------------
// G.19: Breakout Rooms — Host Active Monitoring View
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "@/lib/api-client";
import { SkeletonCard } from "@/components/skeleton";
import type { BreakoutStatusResponse } from "./breakout-types";
import { POLL_INTERVAL_MS, ACTIVITY_COLORS, ACTIVITY_LABELS, formatTime } from "./breakout-types";
import { formatTime as formatDisplayTime } from "@/lib/i18n";

interface BreakoutActiveViewProps {
  webinarId: string;
  breakoutId: string;
  onEnded: () => void;
}

export function BreakoutActiveView({ webinarId, breakoutId, onEnded }: BreakoutActiveViewProps) {
  const [status, setStatus] = useState<BreakoutStatusResponse["data"] | null>(null);
  const [broadcastText, setBroadcastText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch<BreakoutStatusResponse>(
        `/v1/webinars/${webinarId}/breakout/${breakoutId}/status`,
      );
      setStatus(res.data);

      if (res.data.status === "ended") {
        onEnded();
      }

      if (
        res.data.timeRemainingSeconds !== null &&
        res.data.timeRemainingSeconds <= res.data.warningMinutesBefore * 60
      ) {
        setIsWarning(true);
      }
    } catch {
      // Retry on next interval
    }
  }, [webinarId, breakoutId, onEnded]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchStatus]);

  const handleBroadcast = async () => {
    if (!broadcastText.trim() || isSending) return;
    setIsSending(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/breakout/${breakoutId}/broadcast`, {
        method: "POST",
        body: JSON.stringify({ message: broadcastText.trim() }),
      });
      setBroadcastText("");
      await fetchStatus();
    } catch {
      // Error shown via apiFetch throwing
    } finally {
      setIsSending(false);
    }
  };

  const handleStop = async () => {
    if (isStopping) return;
    setIsStopping(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/breakout/${breakoutId}/stop`, {
        method: "POST",
      });
      onEnded();
    } catch {
      setIsStopping(false);
    }
  };

  const handleVisit = async (roomId: string) => {
    try {
      const res = await apiFetch<{ data: { livekitRoom: string } }>(
        `/v1/webinars/${webinarId}/breakout/${breakoutId}/visit/${roomId}`,
        { method: "POST" },
      );
      window.open(`/webinar/${webinarId}/room/${res.data.livekitRoom}`, "_blank");
    } catch {
      // apiFetch handles error display
    }
  };

  if (!status) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonCard key={index} className="h-44" />
        ))}
      </div>
    );
  }

  const totalParticipants = status.rooms.reduce((sum, r) => sum + r.participantCount, 0);

  return (
    <div className="space-y-4">
      {/* Header with timer */}
      <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Breakout Session Active</h2>
          <p className="text-sm text-muted-foreground">
            {status.rooms.length} rooms | {totalParticipants} participants
          </p>
        </div>
        <div className="flex items-center gap-4">
          {status.timeRemainingSeconds !== null && (
            <div className={`text-right ${isWarning ? "animate-pulse" : ""}`}>
              <p className={`text-2xl font-bold tabular-nums ${isWarning ? "text-[var(--color-destructive)]" : "text-foreground"}`}>
                {formatTime(status.timeRemainingSeconds)}
              </p>
              <p className="text-xs text-muted-foreground">remaining</p>
            </div>
          )}
          <button
            onClick={handleStop}
            disabled={isStopping}
            className="rounded-md bg-[var(--color-destructive)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-destructive)] disabled:opacity-50"
          >
            {isStopping ? "Ending..." : "End Breakout"}
          </button>
        </div>
      </div>

      {/* Warning banner */}
      {isWarning && (
        <div className="rounded-md border border-yellow-300 bg-[var(--color-warning)]/5 px-4 py-2 text-sm text-yellow-800">
          Breakout session ending soon. Participants will be notified.
        </div>
      )}

      {/* Room Grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {status.rooms.map((room) => (
          <div key={room.id} className="stagger-item rounded-lg border border-border bg-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">{room.name}</h3>
              <span
                className={`h-2.5 w-2.5 rounded-full ${ACTIVITY_COLORS[room.activityLevel]}`}
                title={ACTIVITY_LABELS[room.activityLevel]}
              />
            </div>
            {room.topic && (
              <p className="mb-3 text-xs text-muted-foreground line-clamp-2">{room.topic}</p>
            )}
            <div className="mb-3 flex items-center gap-1.5">
              <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium text-foreground">{room.participantCount}</span>
              <span className="text-xs text-muted-foreground">{ACTIVITY_LABELS[room.activityLevel]}</span>
            </div>
            <button
              onClick={() => handleVisit(room.id)}
              className="w-full rounded-md border border-border py-1.5 text-sm text-foreground hover:bg-accent"
            >
              Visit Room
            </button>
          </div>
        ))}
      </div>

      {/* Broadcast Bar */}
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3">
        <input
          type="text"
          value={broadcastText}
          onChange={(e) => setBroadcastText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleBroadcast();
            }
          }}
          placeholder="Broadcast a message to all rooms..."
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          maxLength={2000}
        />
        <button
          onClick={handleBroadcast}
          disabled={!broadcastText.trim() || isSending}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSending ? "Sending..." : "Broadcast"}
        </button>
      </div>

      {/* Recent Broadcasts */}
      {status.broadcasts.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-medium text-foreground">Recent Broadcasts</h3>
          <div className="space-y-2">
            {status.broadcasts.slice(-5).reverse().map((b, idx) => (
              <div key={idx} className="stagger-item flex items-start gap-2 text-sm">
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatDisplayTime(b.sentAt)}
                </span>
                <span className="text-foreground">{b.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
